import { ArrowRight } from 'lucide-react';
import { Checkbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';
import {
    getHdeCheckboxClass,
    getHdeMultiCheckboxClassName,
    getHdeMultiLabelClassName,
    getHdeMultiOptionClassName,
    getHdeOptionClassName,
    getHdeOptionDescriptionClassName,
    getHdeStepSectionClassName,
} from './hde-option-styles';

/** Option item with value, label, and optional description (for with-description variant). */
interface HdeOptionListItem {
    value: string;
    label: string;
    /** Descriptive text shown below the label when optionVariant="with-description". */
    description?: string;
}

/** Normalise option to have both value and label. Accepts description or subtitle (deprecated). */
function normaliseOption(
    option:
        | HdeOptionListItem
        | {
              label: string;
              value?: string;
              description?: string;
              subtitle?: string;
          },
): HdeOptionListItem {
    const desc: string | undefined =
        ('description' in option && option.description) ||
        ('subtitle' in option && option.subtitle) ||
        undefined;
    return {
        value: option.value ?? option.label,
        label: option.label,
        description: typeof desc === 'string' ? desc : undefined,
    };
}

interface HdeOptionListBaseProps {
    /** Optional label shown above the option list. */
    label?: string;
    /**
     * 'with-description' shows option description with Figma styling (node 180:4367).
     * Use when options have descriptive text below the label.
     */
    optionVariant?: 'default' | 'with-description';
}

interface HdeOptionListSingleProps extends HdeOptionListBaseProps {
    mode: 'single';
    options: ReadonlyArray<
        | HdeOptionListItem
        | {
              label: string;
              value?: string;
              description?: string;
              subtitle?: string;
          }
    >;
    value: string | null;
    onChange: (value: string) => void;
    /** 'checkbox' | 'button' | 'arrow' - arrow shows chevron on right for single-select */
    appearance?: 'checkbox' | 'button' | 'arrow';
    /** When provided, called after onChange when user selects an option (arrow/button). Use to advance to next step. */
    onSelectAndAdvance?: (value: string) => void;
    /** Optional content to render immediately after a specific option when that option is selected. */
    contentAfterOption?: { optionValue: string; content: React.ReactNode };
}

interface HdeOptionListMultiProps extends HdeOptionListBaseProps {
    mode: 'multi';
    options: ReadonlyArray<
        HdeOptionListItem | { label: string; value?: string }
    >;
    value: ReadonlyArray<HdeOptionListItem>;
    onChange: (selected: HdeOptionListItem[]) => void;
    /** Optional content to render immediately after a specific option when that option is selected. */
    contentAfterOption?: { optionValue: string; content: React.ReactNode };
}

type HdeOptionListProps = HdeOptionListSingleProps | HdeOptionListMultiProps;

/**
 * Reusable HDE-styled option list with checkboxes.
 * Supports single-select (radio-like) and multi-select modes.
 */
export function HdeOptionList(props: HdeOptionListProps) {
    const { options, mode, label, optionVariant = 'default' } = props;
    const normalisedOptions = options.map(normaliseOption);

    const optionList =
        mode === 'single' ? (
            <SingleOptionList
                options={normalisedOptions}
                value={props.value}
                onChange={props.onChange}
                appearance={props.appearance ?? 'checkbox'}
                onSelectAndAdvance={props.onSelectAndAdvance}
                optionVariant={optionVariant}
                contentAfterOption={props.contentAfterOption}
            />
        ) : (
            <MultiOptionList
                options={normalisedOptions}
                value={props.value}
                onChange={props.onChange}
                contentAfterOption={props.contentAfterOption}
            />
        );

    if (label) {
        const labelWrapperClass = getHdeStepSectionClassName();
        const headingClass =
            'text-[1.3rem] font-medium leading-[1.1] tracking-[-0.0263rem] text-[#161616] uppercase';
        return (
            <div className={labelWrapperClass}>
                <label className={headingClass}>{label}</label>
                {optionList}
            </div>
        );
    }

    return optionList;
}

function SingleOptionList({
    options,
    value,
    onChange,
    appearance = 'checkbox',
    onSelectAndAdvance,
    optionVariant = 'default',
    contentAfterOption,
}: {
    options: HdeOptionListItem[];
    value: string | null;
    onChange: (value: string) => void;
    appearance?: 'checkbox' | 'button' | 'arrow';
    onSelectAndAdvance?: (value: string) => void;
    optionVariant?: 'default' | 'with-description';
    contentAfterOption?: { optionValue: string; content: React.ReactNode };
}) {
    /** Matches Figma node 180:4367 – 14.696px (0.9185rem), tracking -0.2939px. Arrow uses font-normal (400). */
    const labelClass =
        appearance === 'arrow'
            ? 'text-[0.9185rem] font-normal leading-[1.1] tracking-[-0.0184rem] text-black'
            : 'text-[0.9185rem] font-medium leading-[1.1] tracking-[-0.0184rem] text-black';
    const useDescriptionVariant = optionVariant === 'with-description';
    const descriptionClass = useDescriptionVariant
        ? getHdeOptionDescriptionClassName()
        : 'text-3 font-normal text-[#858585] tracking-[-0.016rem]';
    /** Figma node 180:4367: 3px (0.1875rem) gap between label and description. */
    const contentGapClass = useDescriptionVariant ? 'gap-0.75' : 'gap-0.5';

    const optionsContainerClass = 'flex flex-col gap-[0.5278rem] pb-40';

    return (
        <div className={optionsContainerClass}>
            {options.map((option) => {
                const isSelected = value === option.value;
                const showContentAfter =
                    contentAfterOption?.optionValue === option.value &&
                    isSelected;
                const optionClassName = getHdeOptionClassName(isSelected);
                const content = (
                    <span
                        className={cn(
                            'flex flex-1 flex-col justify-start text-left',
                            contentGapClass,
                        )}
                    >
                        <span className={labelClass}>{option.label}</span>
                        {option.description && (
                            <span className={descriptionClass}>
                                {option.description}
                            </span>
                        )}
                    </span>
                );

                if (appearance === 'button' || appearance === 'arrow') {
                    const handleClick = () => {
                        onChange(option.value);
                        onSelectAndAdvance?.(option.value);
                    };
                    return (
                        <div
                            key={option.value}
                            className="flex flex-col gap-[0.5278rem]"
                        >
                            <button
                                type="button"
                                onClick={handleClick}
                                className={cn(
                                    optionClassName,
                                    'flex items-center justify-between',
                                )}
                                aria-pressed={isSelected}
                            >
                                {content}
                                {appearance === 'arrow' && (
                                    <ArrowRight className="h-[0.6975rem] shrink-0 text-black" />
                                )}
                            </button>
                            {showContentAfter && contentAfterOption?.content}
                        </div>
                    );
                }

                return (
                    <div
                        key={option.value}
                        className="flex flex-col gap-[0.5278rem]"
                    >
                        <label className={optionClassName}>
                            <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => onChange(option.value)}
                                shape="round"
                                className={getHdeCheckboxClass()}
                            />
                            {content}
                        </label>
                        {showContentAfter && contentAfterOption?.content}
                    </div>
                );
            })}
        </div>
    );
}

function MultiOptionList({
    options,
    value,
    onChange,
    contentAfterOption,
}: {
    options: HdeOptionListItem[];
    value: ReadonlyArray<HdeOptionListItem>;
    onChange: (selected: HdeOptionListItem[]) => void;
    contentAfterOption?: { optionValue: string; content: React.ReactNode };
}) {
    const labelClass = getHdeMultiLabelClassName();
    const optionsContainerClass = 'flex flex-col gap-[0.5278rem] pb-40';

    return (
        <div className={optionsContainerClass}>
            {options.map((option) => {
                const isSelected = value.some((v) => v.value === option.value);
                const showContentAfter =
                    contentAfterOption?.optionValue === option.value &&
                    isSelected;

                return (
                    <div
                        key={option.value}
                        className="flex flex-col gap-[0.5278rem]"
                    >
                        <label
                            className={getHdeMultiOptionClassName(isSelected)}
                        >
                            <Checkbox
                                checked={isSelected}
                                shape="default"
                                onCheckedChange={() => {
                                    const exists = value.find(
                                        (v) => v.value === option.value,
                                    );
                                    if (exists) {
                                        onChange(
                                            value.filter(
                                                (v) => v.value !== option.value,
                                            ),
                                        );
                                    } else {
                                        onChange([...value, option]);
                                    }
                                }}
                                className={getHdeMultiCheckboxClassName()}
                                indicatorClassName="size-2.5 -translate-y-px"
                            />
                            <span className={labelClass}>{option.label}</span>
                        </label>
                        {showContentAfter && contentAfterOption?.content}
                    </div>
                );
            })}
        </div>
    );
}
